import queryString from 'query-string';

// ============================================================================
// Types and Interfaces
// ============================================================================

interface MidwayAuthParams {
    scope: string;
    response_type: string;
    client_id: string;
    redirect_uri: string;
    nonce: string;
}

interface DecodedToken {
    sub: string;
    name?: string;
    email?: string;
    preferred_username?: string;
    exp: number;
    iat: number;
    [key: string]: any;
}

interface AuthResult {
    token: DecodedToken;
    rawToken: string;
}

// ============================================================================
// Constants
// ============================================================================

const MIDWAY_URL = 'https://midway-auth.amazon.com/SSO';
const AUTH_SCOPE = 'openid';
const AUTH_RESPONSE_TYPE = 'id_token';
const TOKEN_STORAGE_KEY = 'midway_token';
const RAW_TOKEN_STORAGE_KEY = 'midway_token_raw';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Determines if the application is running in a local development environment.
 */
const isLocalDevelopment = (): boolean => {
    if (typeof window === 'undefined') {
        console.warn("window is undefined:", window)
        return process.env.NODE_ENV === 'development';
    }
    
    const hostname = window.location.hostname.toLowerCase();
    // const devHosts = ['localhost', '127.0.0.1', '0.0.0.0'];
    const devHosts = ['127.0.0.1', '0.0.0.0'];
    const isDevHost = devHosts.includes(hostname);
    const isDevPort = window.location.port && ['3000', '3001', '4000', '5000', '8000', '8080'].includes(window.location.port);
    
    return isDevHost || hostname.endsWith('.local') || Boolean(isDevPort);
};

/**
 * Generates a random nonce for authentication requests.
 */
const generateRandomNounce = (): string => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * Decodes JWT token payload without verification.
 */
const decodeJWTPayload = (token: string): DecodedToken => {
    try {
        const payload = token.split('.')[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    } catch (error) {
        console.error('Failed to decode JWT payload:', error);
        throw new Error('Invalid token format');
    }
};

/**
 * Checks if a token is valid (not expired).
 */
const isTokenValid = (token: DecodedToken): boolean => {
    if (!token || !token.exp) return false;
    
    const now = Math.floor(Date.now() / 1000);
    const bufferTime = 60; // 60 seconds buffer
    return token.exp > (now + bufferTime);
};

// ============================================================================
// Storage Functions
// ============================================================================

/**
 * Stores the decoded token in localStorage.
 */
const storeToken = (token: DecodedToken): void => {
    try {
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(token));
        console.log('Token stored successfully');
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('midway-token-updated', { detail: token }));
    } catch (error) {
        console.warn('Failed to store token:', error);
    }
};

/**
 * Stores the raw token string in localStorage.
 */
const storeRawToken = (rawToken: string): void => {
    try {
        localStorage.setItem(RAW_TOKEN_STORAGE_KEY, rawToken);
        console.log('Raw token stored in localStorage');
    } catch (fallbackError) {
        console.warn('Failed to store raw token in localStorage fallback:', fallbackError);
    }
};

/**
 * Retrieves the stored token from localStorage.
 */
const getStoredToken = (): DecodedToken | null => {
    try {
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        console.warn('Failed to retrieve stored token:', error);
        return null;
    }
};

/**
 * Retrieves the stored raw token from localStorage.
 */
const getStoredRawToken = (): string | null => {
    try {
        const localStorageToken = localStorage.getItem(RAW_TOKEN_STORAGE_KEY);
        if (localStorageToken) {
            console.log('Raw token retrieved from localStorage');
            return localStorageToken;
        }
        return null;
    } catch (error) {
        console.warn('Failed to retrieve stored raw token:', error);
        return null;
    }
};

/**
 * Clears all stored tokens from localStorage.
 */
const clearStoredTokens = (): void => {
    try {
        // Clear localStorage
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(RAW_TOKEN_STORAGE_KEY);
        console.log('Tokens cleared from storage');
    } catch (error) {
        console.warn('Failed to clear stored tokens:', error);
    }
};

// ============================================================================
// Authentication URL Builders
// ============================================================================

/**
 * Builds a Midway authentication request URL.
 */
const buildMidwayAuthReq = (isRedirectReq = false): string => {
    const paramsObj: MidwayAuthParams = {
        scope: AUTH_SCOPE,
        response_type: AUTH_RESPONSE_TYPE,
        client_id: window.location.origin,
        redirect_uri: document.location.href,
        nonce: generateRandomNounce(),
    };

    const params = queryString.stringify(paramsObj);
    const midwayUrl = isRedirectReq ? `${MIDWAY_URL}/redirect?${params}` : `${MIDWAY_URL}?${params}`;
    
    console.log('Built Midway auth URL:', midwayUrl);
    return midwayUrl;
};


// ============================================================================
// Authentication Functions
// ============================================================================

/**
 * Redirects to Midway authentication.
 */
const beginNewLogin = (): void => {
    console.log('Redirecting to Midway authentication...');
    window.location.assign(buildMidwayAuthReq(true));
};

/**
 * Fetches an identity token from Midway.
 */
const fetchIdentityToken = async (): Promise<string> => {
    const response = await fetch(buildMidwayAuthReq(), { credentials: 'include' });
    if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status} ${response.statusText}`);
    }
    const token = await response.text();
    if (!token) {
        throw new Error('No token received from authentication service');
    }
    return token;
};

// ============================================================================
// Token Management Functions
// ============================================================================

/**
 * Checks for a valid stored token and returns it if found.
 */
const checkForStoredToken = (): DecodedToken | null => {
    console.log('Checking for stored token...');
    
    try {
        const storedToken = getStoredToken();
        
        if (!storedToken) {
            console.log('No stored token found');
            return null;
        }
        
        if (!isTokenValid(storedToken)) {
            console.log('Stored token is expired');
            clearStoredTokens();
            return null;
        }
        
        console.log('Found valid stored token for user:', storedToken.sub);
        return storedToken;
    } catch (error) {
        console.error('Error checking stored token:', error);
        clearStoredTokens();
        return null;
    }
};

/**
 * Checks if user is returning from Midway authentication redirect.
 * Looks for token in URL hash fragment.
 */
const checkForURLRedirectToken = (): DecodedToken | null => {
    console.log('Checking for Midway token in URL...');
    
    try {
        const hash = window.location.hash.substring(1);
        if (!hash) {
            console.log('No hash fragment found in URL');
            return null;
        }
        
        const params = queryString.parse(hash);
        const idToken = params.id_token as string;
        
        if (!idToken) {
            console.log('No id_token found in URL hash');
            return null;
        }
        
        console.log('Found token in URL, decoding...');
        const decodedToken = decodeJWTPayload(idToken);
        
        if (!isTokenValid(decodedToken)) {
            console.log('Token from URL is expired');
            return null;
        }
        
        // Store the token for future use
        storeToken(decodedToken);
        storeRawToken(idToken);
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        
        console.log('Successfully processed token from URL for user:', decodedToken.sub);
        return decodedToken;
    } catch (error) {
        console.error('Error processing token from URL:', error);
        return null;
    }
};

/**
 * Gets the raw token for API authentication.
 * This is a convenience function that retrieves the raw token from cookies.
 */
const getRawTokenForAuth = (): string | null => {
    return getStoredRawToken();
};

/**
 * Debug function to manually set a test token for development/testing.
 * This bypasses the normal authentication flow.
 */
const setTestToken = (testToken: string): void => {
    console.log('Setting test token for development/testing...');
    storeRawToken(testToken);
    
    // Also decode and store the token for compatibility
    try {
        const decoded = decodeJWTPayload(testToken);
        storeToken(decoded);
        console.log('Test token set successfully:', decoded);
    } catch (error) {
        console.warn('Failed to decode test token:', error);
    }
};


/**
 * Gets a Midway token, checking if local dev first, then url redirect token, then stored. If there is no existing token, attempting to fetch new one.
 */
const getMidwayToken = async (): Promise<DecodedToken> => {
    // First check if in local dev env
    console.log('check if local dev env');
    if (isLocalDevelopment()) {
        // For local development, return a mock token
        const mockToken: DecodedToken = {
            sub: 'local-dev-user',
            name: 'Local Dev User',
            email: 'dev@amazon.com',
            exp: Math.floor(Date.now() / 1000) + 3600, // Expires in 1 hour
            iat: Math.floor(Date.now() / 1000)
        };
        storeToken(mockToken);
        console.log('this is local dev env, mock token:', mockToken);
        return mockToken;
    }

    // check for url redirect token
    console.log('check for url redirect token');
    const urlToken = checkForURLRedirectToken();
    if (urlToken) {
        console.log('find url token:', urlToken);
        return urlToken;
    }
    console.log('No valid url redirect token found, checking stored token');

    // check for stored token
    console.log('check for stored token');
    const storedToken = checkForStoredToken();
    if (storedToken) {
        return storedToken;
    }
    console.log('No valid stored token found, fechting token');
    
    try {
        const token = await fetchIdentityToken();
        storeRawToken(token)
        const decodedToken = decodeJWTPayload(token);
        storeToken(decodedToken)
        console.log('Successfully retrieved identity token, decoded token', decodedToken);
        console.log('just want to decode token - ', decodeJWTPayload("eyJ0eXAiOiJKV1MiLCJhbGciOiJQUzI1NiIsIng1dSI6Imh0dHA6Ly9zZW50cnktcGtpLmFtYXpvbi5jb20vcHVibGlja2V5LzI3MTMxNTMyIn0.eyJpc3MiOiJzZW50cnkuYW1hem9uLmNvbSIsInN1YiI6Inlpa2FAQU5ULkFNQVpPTi5DT00iLCJhdWQiOlsiaHR0cHM6Ly9waG9uZXRvb2wuYW1hem9uLmNvbTo0NDMiXSwiZXhwIjoxNzY2MjE3NDE2LCJpYXQiOjE3NjYyMTY1MTYsImF1dGhfdGltZSI6MTc2NjE2NDk4MSwibm9uY2UiOiI3YzhkY2QzMzRjN2EwNmUzYmFkMzVmMzJlNjcxY2Y4NWRjYTE5MTZkMjYxZGRlNzEzZTlmY2FhZDE0YTNlOGM3IiwiYW1yIjpbInUyZiIsIm1mYSIsInB3ZCIsInBlcyJdLCJyZXFfaWQiOiJiNWI2YTdkZS1mMmYxLTRhODAtYWFmMi03YTNlMzk1Njk2ZDkiLCJqdGkiOiJSS2NpYmh5Rll2emJJeU1YMG9tbkp3PT0iLCJwb3N0dXJlX2NoZWNrIjoxLCJtd2kiOjB9.AzqZqrUkPsnWFDXe4Qd7LNeSQOKGP21rHLZuOypPhytswxlkc9b26FYyxK2XgcUdRSROsHzyny0I88qI2sY6eyATRIGav4SO5fd9OLTXIo-Fo1hUHcyr8vA3RH7o4bmwYH16HfOKB_DNUw9Wc2p5CpK6q1u-VbcBwt7eNRn3OBhYpRXswJZygSOTW-DNepL59ovjW_wFfIfM1G9iOU-habS1ZHO0pNpRQ5NzPeelUl2haTCsJYBtnn4uzEsap5eadCLsyCt5m5UeqKmd1mBZxoPwr5FPPX2V3JBtiMattuO6zOsbkK2rpmpETkMfwmNgs60T0ArdkSMBNl7D_faMsQ"))
        return decodedToken
    } catch (error) {
        console.error('Failed to retrieve identity token:', error);
        if (typeof window !== 'undefined') {
            console.log('Redirecting to authentication...');
            beginNewLogin();
            return new Promise<DecodedToken>(() => {}); // Never resolves since we're redirecting
        }
        throw new Error(`Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
};


// ============================================================================
// Exports
// ============================================================================

export {
    // Types
    type DecodedToken,
    type AuthResult,
    
    // Constants
    MIDWAY_URL,
    
    // Utility functions
    isLocalDevelopment,
    generateRandomNounce,
    decodeJWTPayload,
    isTokenValid,
    
    // Storage functions
    storeToken,
    storeRawToken,
    getStoredToken,
    getStoredRawToken,
    clearStoredTokens,
    
    // Auth URL builders
    buildMidwayAuthReq,
    
    // Authentication functions
    beginNewLogin,
    fetchIdentityToken,
    
    // Token management functions
    checkForStoredToken,
    checkForURLRedirectToken,
    getMidwayToken,
    getRawTokenForAuth,
    
    // Debug functions
    setTestToken,
};
